import { BreakpointObserver } from '@angular/cdk/layout';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    IRouterData,
    MotorsDraftFacade,
    RouterFacade,
    StepInfo
} from '@classifieds-sell/state';
import {
    isEditPage,
    isRelistPage
} from '@classifieds-sell/shared';
import { TRADEME_CORE_UI_COMPACT_MAX_WIDTH } from '@classifieds-sell/trademe-core-ui';
import { BehaviorSubject, combineLatest, from, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StepIds } from '../../models/motors-step/motors-step-model';
import { FeeService } from '../../credit-account/fee.service';
import { ClearCreditCardRedirectResponseDataParam, ExtractCreditCardRedirectResponseData } from 'libs/trademe-core-ui/src/lib/ping/ping-widget/credit-card-redirect-helper';
import { DelayService, WindowRef } from '@trademe/tangram';

@Component({
    selector: 'motors-footer',
    templateUrl: './motors-footer.component.html',
    styleUrls: ['./motors-footer.component.scss'],
    standalone: false
})
export class MotorsFooterComponent implements OnInit, OnDestroy, AfterViewInit {
    private _destroyed$: Subject<void> = new Subject<void>();

    @Output() continue = new EventEmitter<StepInfo>();
    @Output() createListing = new EventEmitter<number>();
    @Output() editListing = new EventEmitter<void>();
    @Output() relistListing = new EventEmitter<void>();
    @Output() saveAndExit = new EventEmitter<void>();
    @Output() topUpCompleted = new EventEmitter();

    routerData: IRouterData;

    continueToTitle$ = new BehaviorSubject<string>('');

    listingActionLoading$ = this.draftFacade.listingActionLoading$;
    listingActionResponse$ = this.draftFacade.listingActionResponse$;
    photosUploading$ = this.draftFacade.photosUploading$;
    createOrEditOrRelistListingEnabled: boolean;
    isEditPage = false;
    isRelistPage = false;
    isEditOrRelistPage = false;
    isCreditAccountModalVisible = false;

    isCompact: boolean;
    shouldTopUp = true;

    get isSummaryStep() {
        return this.routerData?.stepInfo?.stepId === StepIds.summary;
    }

    constructor(
        private routerFacade: RouterFacade,
        private draftFacade: MotorsDraftFacade,
        private breakpointObserver: BreakpointObserver,
        private changeDetectorRef: ChangeDetectorRef,
        private route: ActivatedRoute,
        private feeService: FeeService,
        private windowRef: WindowRef,
        private delayService: DelayService
    ) {
    }

    ngAfterViewInit(): void {
        const psd = ExtractCreditCardRedirectResponseData(this.windowRef);
        if (psd) {
            this.windowRef.useWindow(window => {
                this.delayService.createDelay(1000).subscribe(() => {
                    window.scrollTo(0, window.document.body.scrollHeight);
                    this.isCreditAccountModalVisible = true;
                    this.changeDetectorRef.detectChanges();
                });
            });
        }
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
    }

    ngOnInit(): void {
        from(this.routerFacade.routerData$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((routerStateData) => {
                this.routerStateSubscriber(routerStateData);
            });

        combineLatest([
            this.routerFacade.routerData$,
            this.draftFacade.validationResults$,
            this.feeService.isTopUpRequired$
        ])
            .pipe(takeUntil(this._destroyed$))
            .subscribe(
                ([routerStateData, validationResults, isTopUpRequired]) => {
                    this.routerStateSubscriber(routerStateData);

                    if (this.isSummaryStep) {
                        this.shouldTopUp = isTopUpRequired;
                        const filteredValidationResults =
                            validationResults?.filter(
                                (v) => v.stepId !== StepIds.summary
                            ) || [];
                        this.createOrEditOrRelistListingEnabled =
                            filteredValidationResults.length > 0 &&
                            filteredValidationResults.every((v) => v.isValid);
                    }
                }
            );

        this.breakpointObserver
            .observe([TRADEME_CORE_UI_COMPACT_MAX_WIDTH])
            .subscribe((result) => {
                this.isSmallChange(result.matches);
            });

        this.isRelistPage = isRelistPage(this.route, true);
        this.isEditPage = isEditPage(this.route, true);
        this.isEditOrRelistPage = this.isEditPage || this.isRelistPage;
    }

    private isSmallChange(isSmall: boolean) {
        this.isCompact = isSmall;
        this.changeDetectorRef.markForCheck();
    }

    private routerStateSubscriber(routerStateData: IRouterData): void {
        this.routerData = routerStateData;

        if (routerStateData && this.routerData.stepInfo) {
            if (routerStateData.stepInfo.nextStep) {
                this.continueToTitle$.next(
                    routerStateData.stepInfo.nextStep.title.toLocaleLowerCase()
                );
            }
        }
    }

    onContinueTo() {
        this.continue.emit(this.routerData.stepInfo);
    }

    onCreateListing() {
        this.createListing.emit(this.routerData.categoryId);
    }

    onSaveAndExit() {
        this.saveAndExit.emit();
    }

    onEditListing() {
        this.editListing.emit();
    }

    onRelistListing() {
        this.relistListing.emit();
    }

    onEditOrRelistListing() {
        if (this.isEditPage) {
            this.onEditListing();
        } else if (this.isRelistPage) {
            this.onRelistListing();
        }
    }

    onShowTopUpModal(showTopUpModal: boolean): void {
        this.isCreditAccountModalVisible = showTopUpModal;
    }

    onCreditAdded(): void {
        this.onShowTopUpModal(false);
        this.topUpCompleted.emit();
        ClearCreditCardRedirectResponseDataParam(this.windowRef);
    }

    onCreditFailure(): void {
        this.onShowTopUpModal(false);
    }
}
